import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Package,
  Users,
  FileText,
  UserPlus,
  Map,
  Truck as TruckIcon,
  CheckCircle2,
  Clock,
  AlertCircle,
  TrendingUp,
  Activity,
  Loader2,
  RefreshCw,
  Route,
  CalendarDays,
  AlertTriangle,
  CheckSquare,
  AlertOctagon,
  ShieldAlert,
  X,
  ChevronRight,
  Building2,
  MapPin,
  Eye,
  Navigation2,
  Plane,
  Ship,
  Calculator,
  Layers,
  Globe,
  Sparkles,
  ArrowUpRight,
  Store,
  BarChart3,
  PieChart as PieChartIcon,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import VehicleTrackModal from '../components/VehicleTrackModal';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
});

const DOC_ICONS = {
  PUC: '🔬',
  INSURANCE: '🛡️',
  FITNESS: '✅',
  PERMIT: '📋',
};

// ─── Status Badge Color Helper ────────────────────────────────────────────────
const statusColor = (status) => {
  const s = (status || '').toUpperCase();
  if (['DELIVERED', 'COMPLETED', 'CONFIRMED', 'ACCEPTED'].includes(s)) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (['IN_TRANSIT', 'ACTIVE', 'IN TRANSIT', 'SENT'].includes(s)) return 'bg-blue-50 text-blue-700 border-blue-200';
  if (['PENDING', 'DRAFT'].includes(s)) return 'bg-amber-50 text-amber-700 border-amber-200';
  if (['CANCELLED', 'REJECTED', 'EXPIRED'].includes(s)) return 'bg-red-50 text-red-700 border-red-200';
  return 'bg-gray-100 text-gray-700 border-gray-200';
};

const formatNumber = (n) => (n || 0).toLocaleString('en-IN');

// ─── Custom Tooltip for Monthly Volume Bar Chart ──────────────────────────────
const CustomBarTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const fullMonth = payload[0]?.payload?.full_month || label;
    return (
      <div className="bg-slate-900/95 text-white p-2.5 rounded-lg shadow-xl text-xs font-ibm border border-slate-700 backdrop-blur-sm">
        <p className="font-semibold text-slate-200 border-b border-slate-700/60 pb-1 mb-1.5">{fullMonth}</p>
        <div className="space-y-1">
          {payload.map((entry, index) => (
            <div key={`item-${index}`} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                <span className="text-slate-300">{entry.name}:</span>
              </div>
              <span className="font-bold text-white">{entry.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

// ─── Custom Tooltip for Mode Distribution Donut Chart ─────────────────────────
const CustomPieTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    return (
      <div className="bg-slate-900/95 text-white px-2.5 py-1.5 rounded-lg shadow-xl text-xs font-ibm border border-slate-700">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: data.payload.color }} />
          <span className="font-medium text-slate-200">{data.name}:</span>
          <span className="font-bold text-white">{data.value}</span>
        </div>
      </div>
    );
  }
  return null;
};

// ─── Stat Card Component (Clean, Focus on Numbers & Counts) ───────────────────
const StatCard = ({ label, value, icon: Icon, color, sub, onClick }) => (
  <Card
    className={`p-5 bg-white rounded-xl border border-gray-100 shadow-sm transition-all ${
      onClick ? 'cursor-pointer hover:shadow-md hover:border-accent-blue/30 hover:-translate-y-0.5' : ''
    }`}
    onClick={onClick}
    data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-start justify-between">
      <div className="flex-1 min-w-0 pr-2">
        <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm mb-1 truncate">
          {label}
        </p>
        <h3 className="text-3xl font-bold text-text-dark font-manrope" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
          {value}
        </h3>
        {sub && <p className="text-xs text-text-secondary font-ibm mt-1.5 truncate">{sub}</p>}
      </div>
      <div className={`w-11 h-11 ${color} rounded-xl flex items-center justify-center flex-shrink-0 shadow-xs`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </Card>
);

// ─── Daily Task Flash Banner ──────────────────────────────────────────────────
const TaskFlashBanner = ({ taskSummary, onDismiss, onOpenBriefing, navigate }) => {
  if (!taskSummary) return null;
  const { overdue_count, due_today_count, my_open_count } = taskSummary;
  if ((overdue_count || 0) === 0 && (due_today_count || 0) === 0) return null;

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-3.5 shadow-xs" data-testid="task-flash-section">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-600 text-white flex items-center justify-center shrink-0">
            <AlertOctagon className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-slate-900 text-sm font-manrope">
                Daily Operations Briefing
              </span>
              {overdue_count > 0 && (
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-600 text-white">
                  {overdue_count} Overdue
                </span>
              )}
              {due_today_count > 0 && (
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-600 text-white">
                  {due_today_count} Due Today
                </span>
              )}
            </div>
            <p className="text-xs text-slate-600 mt-0.5 font-ibm">
              {overdue_count > 0 ? `${overdue_count} task(s) overdue. ` : ''}
              {due_today_count > 0 ? `${due_today_count} task(s) scheduled for today. ` : ''}
              {my_open_count > 0 ? `(${my_open_count} assigned to you).` : ''}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            variant="outline"
            onClick={onOpenBriefing}
            className="bg-white text-slate-700 border-amber-300 hover:bg-amber-100/60 text-xs h-7 px-2.5 font-medium flex items-center gap-1.5"
          >
            <CheckSquare className="w-3.5 h-3.5 text-blue-600" />
            Quick Briefing
          </Button>
          <Button
            size="sm"
            onClick={() => navigate('/tasks')}
            className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7 px-2.5 font-medium flex items-center gap-1"
          >
            Task Board →
          </Button>
          <button
            onClick={onDismiss}
            className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-200/50"
            title="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Daily Briefing Modal ─────────────────────────────────────────────────────
const DailyBriefingModal = ({ open, onClose, summary, navigate, onTaskCompleted }) => {
  if (!summary) return null;
  const overdue = summary.overdue_tasks || [];
  const today = summary.today_tasks || [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto font-ibm">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center">
              <CheckSquare className="w-4 h-4" />
            </div>
            <div>
              <DialogTitle className="font-manrope text-lg">Daily Operations Briefing</DialogTitle>
              <p className="text-xs text-slate-500">Actionable operational tasks scheduled for today and overdue milestones.</p>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {overdue.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <AlertOctagon className="w-4 h-4 text-red-600" />
                <h4 className="text-xs font-bold text-red-700 uppercase tracking-wider font-manrope">
                  Overdue Tasks ({overdue.length})
                </h4>
              </div>
              <div className="space-y-2">
                {overdue.map(t => (
                  <div key={t.id} className="p-3 bg-red-50/60 border border-red-200 rounded-lg flex items-center justify-between gap-3 text-xs">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-slate-700">{t.task_number}</span>
                        {t.related_entity_ref && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-white text-slate-600 border">
                            {t.related_entity_ref}
                          </span>
                        )}
                        <span className="text-[10px] text-red-600 font-semibold">Due: {t.due_date}</span>
                      </div>
                      <div className="font-medium text-slate-900 mt-0.5">{t.title}</div>
                      <div className="text-[11px] text-slate-500">Assigned: {t.assigned_to_name || 'Unassigned'}</div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onTaskCompleted(t.id)}
                      className="h-7 text-xs bg-white text-green-700 border-green-300 hover:bg-green-50 shrink-0 font-medium"
                    >
                      Mark Done ✓
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {today.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-amber-600" />
                <h4 className="text-xs font-bold text-amber-700 uppercase tracking-wider font-manrope">
                  Due Today ({today.length})
                </h4>
              </div>
              <div className="space-y-2">
                {today.map(t => (
                  <div key={t.id} className="p-3 bg-amber-50/60 border border-amber-200 rounded-lg flex items-center justify-between gap-3 text-xs">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-slate-700">{t.task_number}</span>
                        {t.related_entity_ref && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-white text-slate-600 border">
                            {t.related_entity_ref}
                          </span>
                        )}
                      </div>
                      <div className="font-medium text-slate-900 mt-0.5">{t.title}</div>
                      <div className="text-[11px] text-slate-500">Assigned: {t.assigned_to_name || 'Unassigned'}</div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onTaskCompleted(t.id)}
                      className="h-7 text-xs bg-white text-green-700 border-green-300 hover:bg-green-50 shrink-0 font-medium"
                    >
                      Mark Done ✓
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {overdue.length === 0 && today.length === 0 && (
            <div className="text-center py-6 text-slate-500 text-xs">
              No pending tasks for today! All tasks are up to date.
            </div>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between pt-3 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              navigate('/tasks');
              onClose();
            }}
            className="text-xs text-blue-700"
          >
            Go to Task Board (TMS) →
          </Button>
          <Button size="sm" onClick={onClose} className="bg-blue-600 hover:bg-blue-700 text-white text-xs">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main Dashboard Component ─────────────────────────────────────────────────
export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expiryAlerts, setExpiryAlerts] = useState([]);
  const [dismissedExpiry, setDismissedExpiry] = useState(false);
  const [taskSummary, setTaskSummary] = useState(null);
  const [dismissedTaskFlash, setDismissedTaskFlash] = useState(false);
  const [briefingModalOpen, setBriefingModalOpen] = useState(false);

  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [trackingTrip, setTrackingTrip] = useState(null);

  // Quick mark task done
  const handleQuickTaskDone = async (taskId) => {
    try {
      const res = await fetch(`${API}/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ status: 'completed' }),
      });
      if (res.ok) {
        fetch(`${API}/api/tasks/summary/dashboard`, { headers: authHeaders() })
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (d) setTaskSummary(d); });
      }
    } catch {}
  };

  // Auth & Super Admin detection
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.role === 'super_admin') {
          setIsSuperAdmin(true);
          fetch(`${API}/api/companies`, { headers: authHeaders() })
            .then(r => r.ok ? r.json() : [])
            .then(setCompanies)
            .catch(() => {});
        }
      } catch {}
    }
  }, []);

  // Fetch Dashboard Stats
  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (selectedCompany !== 'all') params.set('company_id_filter', selectedCompany);
      const res = await fetch(`${API}/api/dashboard/stats?${params}`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      setStats(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany]);

  // Fetch Alerts & Task Summary
  const fetchAlerts = useCallback(async () => {
    try {
      const expiryRes = await fetch(`${API}/api/notifications/expiry-alerts?days=30`, { headers: authHeaders() });
      if (expiryRes.ok) setExpiryAlerts(await expiryRes.json());
      fetch(`${API}/api/tasks/summary/dashboard`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setTaskSummary(d); })
        .catch(() => {});
    } catch {}
  }, []);

  useEffect(() => {
    fetchStats();
    fetchAlerts();
  }, [fetchStats, fetchAlerts]);

  // ─── Determine Active Company Type ──────────────────────────────────────────
  const activeCompany = stats?.active_company;
  const isAllCompanies = selectedCompany === 'all' && isSuperAdmin;

  // Check company operational mode
  // SLPL: ROAD, RAIL, AIR -> surface & fleet
  // SILLP: AIR, SEA, COURIER -> international freight forwarding
  const isLogisticsCompany = useMemo(() => {
    if (isAllCompanies) return false;
    const types = activeCompany?.allowed_shipment_types || [];
    return types.includes('ROAD') || types.includes('RAIL');
  }, [activeCompany, isAllCompanies]);

  const isInternationalCompany = useMemo(() => {
    if (isAllCompanies) return false;
    const types = activeCompany?.allowed_shipment_types || [];
    return types.includes('SEA') || (types.includes('AIR') && !types.includes('ROAD'));
  }, [activeCompany, isAllCompanies]);

  // Compute Freight Mode Split Donut Data
  const { modeChartData, totalModalCount } = useMemo(() => {
    const byType = stats?.dockets?.by_type || {};
    let data = [];
    if (isLogisticsCompany) {
      data = [
        { name: 'Road Freight', value: byType.ROAD || 0, color: '#f97316' },
        { name: 'Rail Freight', value: byType.RAIL || 0, color: '#dc2626' },
        { name: 'Domestic Air', value: byType.AIR || 0, color: '#06b6d4' },
      ];
    } else if (isInternationalCompany) {
      data = [
        { name: 'Air Cargo', value: byType.AIR || 0, color: '#06b6d4' },
        { name: 'Ocean Freight', value: byType.SEA || 0, color: '#2563eb' },
      ];
    } else {
      data = [
        { name: 'Road Freight', value: byType.ROAD || 0, color: '#f97316' },
        { name: 'Air Cargo', value: byType.AIR || 0, color: '#06b6d4' },
        { name: 'Ocean Freight', value: byType.SEA || 0, color: '#2563eb' },
        { name: 'Rail Freight', value: byType.RAIL || 0, color: '#dc2626' },
      ];
    }
    const filtered = data.filter(d => d.value > 0);
    const total = filtered.reduce((acc, d) => acc + d.value, 0);
    return { modeChartData: filtered, totalModalCount: total };
  }, [stats, isLogisticsCompany, isInternationalCompany]);

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-accent-blue mx-auto mb-3" />
          <p className="text-text-secondary font-ibm text-sm">Loading operations overview...</p>
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-accent-red mx-auto mb-3" />
          <p className="text-text-dark font-semibold font-ibm mb-1">Failed to load dashboard</p>
          <p className="text-text-secondary text-sm font-ibm mb-4">{error}</p>
          <Button onClick={fetchStats} variant="outline" size="sm" className="font-ibm">
            <RefreshCw className="w-4 h-4 mr-2" /> Retry
          </Button>
        </div>
      </div>
    );
  }

  const d = stats || {
    dockets: { total: 0, by_type: {} },
    estimates: { total: 0, by_status: {} },
    customers: { total: 0 },
    vendors: { total: 0 },
    fleet: { vehicles: 0, drivers: 0 },
    trips: { total: 0, active: 0, completed: 0 },
    bookings: { total: 0, pending: 0, confirmed: 0 },
    monthly_trend: [],
    recent_dockets: [],
    active_shipments: [],
    live_trips: [],
    recent_trips: [],
    recent_bookings: [],
    recent_estimates: [],
  };

  // Consolidated activity stream
  const activityItems = [
    ...(d.recent_trips || []).map(t => ({
      key: `trip-${t.id}`,
      title: `Trip ${t.trip_number}`,
      subtitle: `${t.from_location || '?'} → ${t.to_location || '?'} · ${t.vehicle_registration || ''}`,
      status: t.status,
      time: t.trip_date,
      type: 'trip',
      icon: TruckIcon,
      onClick: () => navigate('/fleet/trips'),
    })),
    ...(d.recent_bookings || []).map(b => ({
      key: `booking-${b.id}`,
      title: `Booking ${b.inquiry_number}`,
      subtitle: `${b.customer_name || 'Customer'} · ${b.from_location_name || ''} → ${b.to_location_name || ''}`,
      status: b.status,
      time: b.inquiry_date,
      type: 'booking',
      icon: Package,
      onClick: () => navigate('/booking'),
    })),
    ...(d.recent_estimates || []).map(e => ({
      key: `est-${e.id}`,
      title: `Estimate ${e.estimate_no}`,
      subtitle: `${e.customer_name || 'Customer'} · ${e.mode_of_transport || 'Quote'}`,
      status: e.status,
      time: e.created_at ? e.created_at.split('T')[0] : '',
      type: 'estimate',
      icon: Calculator,
      onClick: () => navigate(`/estimates/edit/${e.id}`),
    })),
  ]
    .filter(item => item.time)
    .sort((a, b) => new Date(b.time) - new Date(a.time))
    .slice(0, 6);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* ─── Header & Company Filter ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-4 flex-wrap pb-2 border-b border-gray-100">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-bold text-text-dark font-manrope">Operations Dashboard</h1>
            {activeCompany && (
              <Badge variant="outline" className="text-xs font-ibm bg-blue-50 text-accent-blue border-blue-200">
                {activeCompany.code || activeCompany.name}
              </Badge>
            )}
          </div>
          <p className="text-text-secondary font-ibm text-xs mt-0.5">
            {isLogisticsCompany
              ? 'Domestic Surface Freight & Fleet Operations'
              : isInternationalCompany
              ? 'International Freight Forwarding, Cargo & Quotations'
              : 'Multi-Company Consolidated Operations Overview'}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Super Admin company selector */}
          {isSuperAdmin && companies.length > 0 && (
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-text-secondary" />
              <select
                data-testid="dashboard-company-filter"
                value={selectedCompany}
                onChange={e => setSelectedCompany(e.target.value)}
                className="h-9 border border-gray-200 rounded-lg px-3 text-xs font-ibm bg-white focus:outline-none focus:ring-2 focus:ring-accent-blue min-w-[210px] text-slate-700 font-medium"
              >
                <option value="all">🏢 All Companies (Consolidated)</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.code ? `[${c.code}] ` : ''}{c.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={fetchStats}
            className="font-ibm text-text-secondary hover:text-text-dark h-9 text-xs"
            data-testid="refresh-dashboard-btn"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Refresh
          </Button>
        </div>
      </div>

      {/* ─── Operational Alerts Strip (Tasks + Vehicle Compliance) ───────────── */}
      {!dismissedTaskFlash && taskSummary && (
        <TaskFlashBanner
          taskSummary={taskSummary}
          onDismiss={() => setDismissedTaskFlash(true)}
          onOpenBriefing={() => setBriefingModalOpen(true)}
          navigate={navigate}
        />
      )}

      {/* Vehicle Compliance Alert (Only relevant for Logistics / All Companies) */}
      {!dismissedExpiry && !isInternationalCompany && expiryAlerts.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50/70 p-3 flex items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-2.5">
            <ShieldAlert className="w-4 h-4 text-red-600 shrink-0" />
            <span className="font-semibold text-red-800 font-ibm">
              Fleet Compliance: {expiryAlerts.length} vehicle document{expiryAlerts.length > 1 ? 's' : ''} expiring soon (PUC / Fitness / Insurance)
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => navigate('/fleet/vehicles')}
              className="text-xs text-red-700 font-semibold underline hover:text-red-900"
            >
              Review Vehicles →
            </button>
            <button onClick={() => setDismissedExpiry(true)} className="text-red-400 hover:text-red-600 p-0.5">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Daily Briefing Modal */}
      <DailyBriefingModal
        open={briefingModalOpen}
        onClose={() => setBriefingModalOpen(false)}
        summary={taskSummary}
        navigate={navigate}
        onTaskCompleted={handleQuickTaskDone}
      />

      {/* ─── Top 4 KPI Operational Cards (Company-Tailored, NO Financials) ──── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {isLogisticsCompany ? (
          // STAVAN LOGISTICS: Surface & Fleet metrics
          <>
            <StatCard
              label="Active Trips"
              value={formatNumber(d.trips?.active)}
              icon={TruckIcon}
              color="bg-emerald-50 text-emerald-600"
              sub={`${formatNumber(d.trips?.completed)} completed · ${formatNumber(d.trips?.total)} total`}
              onClick={() => navigate('/fleet/trips?status=ACTIVE')}
            />
            <StatCard
              label="Domestic Dockets"
              value={formatNumber((d.dockets?.by_type?.ROAD || 0) + (d.dockets?.by_type?.RAIL || 0))}
              icon={FileText}
              color="bg-blue-50 text-blue-600"
              sub={`Road: ${d.dockets?.by_type?.ROAD || 0} | Rail: ${d.dockets?.by_type?.RAIL || 0}`}
              onClick={() => navigate('/dockets')}
            />
            <StatCard
              label="Active Fleet"
              value={formatNumber(d.fleet?.vehicles)}
              icon={Layers}
              color="bg-purple-50 text-purple-600"
              sub={`${formatNumber(d.fleet?.drivers)} active drivers registered`}
              onClick={() => navigate('/fleet/vehicles')}
            />
            <StatCard
              label="Pending Bookings"
              value={formatNumber(d.bookings?.pending)}
              icon={Clock}
              color="bg-amber-50 text-amber-600"
              sub={`${formatNumber(d.bookings?.confirmed)} confirmed pickups`}
              onClick={() => navigate('/booking?status=PENDING')}
            />
          </>
        ) : isInternationalCompany ? (
          // STAVAN INTERNATIONAL: Forwarding, Air/Ocean & Quotations
          <>
            <StatCard
              label="International Dockets"
              value={formatNumber(d.dockets?.total)}
              icon={Globe}
              color="bg-blue-50 text-blue-600"
              sub={`Air: ${d.dockets?.by_type?.AIR || 0} | Sea: ${d.dockets?.by_type?.SEA || 0}`}
              onClick={() => navigate('/dockets')}
            />
            <StatCard
              label="Quotations & Estimates"
              value={formatNumber(d.estimates?.total)}
              icon={Calculator}
              color="bg-purple-50 text-purple-600"
              sub={`${d.estimates?.by_status?.SENT || 0} Sent · ${d.estimates?.by_status?.ACCEPTED || 0} Accepted`}
              onClick={() => navigate('/estimates')}
            />
            <StatCard
              label="Air Cargo (AWB)"
              value={formatNumber(d.dockets?.by_type?.AIR)}
              icon={Plane}
              color="bg-cyan-50 text-cyan-600"
              sub="Air Waybill Shipments"
              onClick={() => navigate('/dockets?shipment_type=AIR')}
            />
            <StatCard
              label="Ocean Cargo (FCL/LCL)"
              value={formatNumber(d.dockets?.by_type?.SEA)}
              icon={Ship}
              color="bg-emerald-50 text-emerald-600"
              sub="Container Ocean Shipments"
              onClick={() => navigate('/dockets?shipment_type=SEA')}
            />
          </>
        ) : (
          // ALL COMPANIES (CONSOLIDATED / SUPER ADMIN)
          <>
            <StatCard
              label="Total Shipments"
              value={formatNumber(d.dockets?.total)}
              icon={FileText}
              color="bg-blue-50 text-blue-600"
              sub={`Road: ${d.dockets?.by_type?.ROAD || 0} | Air: ${d.dockets?.by_type?.AIR || 0} | Sea: ${d.dockets?.by_type?.SEA || 0}`}
              onClick={() => navigate('/dockets')}
            />
            <StatCard
              label="Active Fleet Trips"
              value={formatNumber(d.trips?.active)}
              icon={TruckIcon}
              color="bg-emerald-50 text-emerald-600"
              sub={`${formatNumber(d.trips?.completed)} completed trips`}
              onClick={() => navigate('/fleet/trips?status=ACTIVE')}
            />
            <StatCard
              label="Active Estimates"
              value={formatNumber(d.estimates?.total)}
              icon={Calculator}
              color="bg-purple-50 text-purple-600"
              sub={`${d.estimates?.by_status?.SENT || 0} Sent · ${d.estimates?.by_status?.ACCEPTED || 0} Accepted`}
              onClick={() => navigate('/estimates')}
            />
            <StatCard
              label="Pending Bookings"
              value={formatNumber(d.bookings?.pending)}
              icon={Clock}
              color="bg-amber-50 text-amber-600"
              sub={`${formatNumber(d.bookings?.confirmed)} confirmed orders`}
              onClick={() => navigate('/booking?status=PENDING')}
            />
          </>
        )}
      </div>

      {/* ─── Main Middle Section: Entity Hub & Quick Operations ────────────── */}
      {isAllCompanies ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Stavan Logistics Summary Card */}
          <Card className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between pb-2.5 border-b border-gray-100">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-7 h-7 rounded-lg bg-orange-100 text-orange-700 flex items-center justify-center font-bold text-xs font-manrope shrink-0">
                    SLPL
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-xs font-bold text-slate-900 font-manrope truncate">Stavan Logistics</h4>
                    <p className="text-[10px] text-slate-400 font-ibm truncate">Surface Freight & Fleet</p>
                  </div>
                </div>
                <Badge variant="outline" className="text-[9px] bg-orange-50 text-orange-700 border-orange-200 shrink-0 px-1.5 py-0.2">
                  Surface
                </Badge>
              </div>

              <div className="grid grid-cols-3 gap-1.5 py-3 text-center">
                <div className="bg-slate-50/80 rounded-lg p-2">
                  <p className="text-[10px] text-slate-500 font-ibm font-medium">Dockets</p>
                  <p className="text-base font-bold text-slate-900 font-manrope">
                    {stats?.company_breakdown?.find(c => c.code === 'SLPL')?.dockets || 0}
                  </p>
                </div>
                <div className="bg-slate-50/80 rounded-lg p-2">
                  <p className="text-[10px] text-slate-500 font-ibm font-medium">Trips</p>
                  <p className="text-base font-bold text-slate-900 font-manrope">
                    {stats?.company_breakdown?.find(c => c.code === 'SLPL')?.trips || 0}
                  </p>
                </div>
                <div className="bg-slate-50/80 rounded-lg p-2">
                  <p className="text-[10px] text-slate-500 font-ibm font-medium">Estimates</p>
                  <p className="text-base font-bold text-slate-900 font-manrope">
                    {stats?.company_breakdown?.find(c => c.code === 'SLPL')?.estimates || 0}
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setSelectedCompany('26182c09-6399-45d1-b0be-1e4b539a350e')}
              className="w-full text-[11px] font-semibold text-accent-blue bg-blue-50/70 hover:bg-blue-100/70 py-1.5 rounded-lg transition-colors flex items-center justify-center gap-1 font-ibm mt-1"
            >
              View Logistics Dashboard →
            </button>
          </Card>

          {/* Stavan International Summary Card */}
          <Card className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between pb-2.5 border-b border-gray-100">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-7 h-7 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs font-manrope shrink-0">
                    SILLP
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-xs font-bold text-slate-900 font-manrope truncate">Stavan International</h4>
                    <p className="text-[10px] text-slate-400 font-ibm truncate">Air & Ocean Forwarding</p>
                  </div>
                </div>
                <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-700 border-blue-200 shrink-0 px-1.5 py-0.2">
                  Forwarding
                </Badge>
              </div>

              <div className="grid grid-cols-3 gap-1.5 py-3 text-center">
                <div className="bg-slate-50/80 rounded-lg p-2">
                  <p className="text-[10px] text-slate-500 font-ibm font-medium">Dockets</p>
                  <p className="text-base font-bold text-slate-900 font-manrope">
                    {stats?.company_breakdown?.find(c => c.code === 'SILLP')?.dockets || 0}
                  </p>
                </div>
                <div className="bg-slate-50/80 rounded-lg p-2">
                  <p className="text-[10px] text-slate-500 font-ibm font-medium">Quotes</p>
                  <p className="text-base font-bold text-slate-900 font-manrope">
                    {stats?.company_breakdown?.find(c => c.code === 'SILLP')?.estimates || 0}
                  </p>
                </div>
                <div className="bg-slate-50/80 rounded-lg p-2">
                  <p className="text-[10px] text-slate-500 font-ibm font-medium">Air / Sea</p>
                  <p className="text-base font-bold text-slate-900 font-manrope">
                    {(d.dockets?.by_type?.AIR || 0) + (d.dockets?.by_type?.SEA || 0)}
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setSelectedCompany('74dbf97c-19a0-4892-aa7a-df06a148a580')}
              className="w-full text-[11px] font-semibold text-accent-blue bg-blue-50/70 hover:bg-blue-100/70 py-1.5 rounded-lg transition-colors flex items-center justify-center gap-1 font-ibm mt-1"
            >
              View International Dashboard →
            </button>
          </Card>

          {/* Estimates Funnel Card */}
          <Card className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between pb-2.5 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <Calculator className="w-4 h-4 text-purple-600" />
                  <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider font-manrope">
                    Estimates Funnel
                  </h4>
                </div>
                <button
                  onClick={() => navigate('/estimates')}
                  className="text-[11px] text-accent-blue font-semibold hover:underline font-ibm"
                >
                  View ({d.estimates?.total || 0})
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2 py-2.5 text-center">
                <div className="bg-slate-50/80 p-2 rounded-lg border border-gray-100">
                  <p className="text-[10px] font-semibold text-slate-500 uppercase font-ibm">Drafts</p>
                  <p className="text-base font-bold text-slate-800 font-manrope">{d.estimates?.by_status?.DRAFT || 0}</p>
                </div>
                <div className="bg-blue-50/70 p-2 rounded-lg border border-blue-100">
                  <p className="text-[10px] font-semibold text-blue-600 uppercase font-ibm">Sent</p>
                  <p className="text-base font-bold text-blue-900 font-manrope">{d.estimates?.by_status?.SENT || 0}</p>
                </div>
                <div className="bg-emerald-50/70 p-2 rounded-lg border border-emerald-100">
                  <p className="text-[10px] font-semibold text-emerald-600 uppercase font-ibm">Accepted</p>
                  <p className="text-base font-bold text-emerald-900 font-manrope">{d.estimates?.by_status?.ACCEPTED || 0}</p>
                </div>
                <div className="bg-red-50/50 p-2 rounded-lg border border-red-100">
                  <p className="text-[10px] font-semibold text-red-500 uppercase font-ibm">Rejected</p>
                  <p className="text-base font-bold text-red-800 font-manrope">{d.estimates?.by_status?.REJECTED || 0}</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Quick Operations Card */}
          <Card className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between pb-2.5 border-b border-gray-100">
                <h3 className="text-xs font-bold text-slate-900 font-manrope uppercase tracking-wider">
                  Quick Operations
                </h3>
                <span className="text-[10px] text-slate-400 font-ibm">Shortcuts</span>
              </div>

              <div className="grid grid-cols-2 gap-2 py-2">
                <button
                  onClick={() => navigate('/dockets/create')}
                  className="p-2.5 bg-blue-50/70 hover:bg-blue-100 text-blue-800 rounded-lg flex flex-col items-center justify-center gap-1 transition-colors font-ibm text-[11px] font-semibold border border-blue-100 text-center"
                >
                  <FileText className="w-4 h-4 text-blue-600" />
                  <span>New Docket</span>
                </button>
                <button
                  onClick={() => navigate('/fleet/trips/add')}
                  className="p-2.5 bg-emerald-50/70 hover:bg-emerald-100 text-emerald-800 rounded-lg flex flex-col items-center justify-center gap-1 transition-colors font-ibm text-[11px] font-semibold border border-emerald-100 text-center"
                >
                  <TruckIcon className="w-4 h-4 text-emerald-600" />
                  <span>Create Trip</span>
                </button>
                <button
                  onClick={() => navigate('/booking/add')}
                  className="p-2.5 bg-amber-50/70 hover:bg-amber-100 text-amber-800 rounded-lg flex flex-col items-center justify-center gap-1 transition-colors font-ibm text-[11px] font-semibold border border-amber-100 text-center"
                >
                  <Package className="w-4 h-4 text-amber-600" />
                  <span>New Booking</span>
                </button>
                <button
                  onClick={() => navigate('/estimates/add')}
                  className="p-2.5 bg-purple-50/70 hover:bg-purple-100 text-purple-800 rounded-lg flex flex-col items-center justify-center gap-1 transition-colors font-ibm text-[11px] font-semibold border border-purple-100 text-center"
                >
                  <Calculator className="w-4 h-4 text-purple-600" />
                  <span>New Estimate</span>
                </button>
              </div>
            </div>
          </Card>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {isLogisticsCompany ? (
              // LOGISTICS: Live Dispatch & Active Trips Monitor
              <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden h-full flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                      <h3 className="text-base font-semibold text-text-dark font-manrope">Live Fleet & Active Trips</h3>
                      {d.trips?.active > 0 && (
                        <span className="text-[11px] bg-emerald-100 text-emerald-700 font-bold px-2 py-0.5 rounded-full font-ibm">
                          {d.trips.active} running
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => navigate('/fleet/trips')}
                      className="text-xs text-accent-blue hover:text-accent-blue/80 font-ibm font-medium flex items-center gap-1"
                    >
                      View all <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>

                  <div className="divide-y divide-gray-50">
                    {(!d.live_trips || d.live_trips.length === 0) ? (
                      <div className="flex flex-col items-center justify-center py-10 text-center">
                        <TruckIcon className="w-8 h-8 text-gray-300 mb-2" />
                        <p className="text-gray-500 font-ibm text-xs font-medium">No vehicles currently on trip</p>
                        <button
                          onClick={() => navigate('/fleet/trips/add')}
                          className="mt-3 text-xs text-accent-blue border border-accent-blue/30 px-3 py-1 rounded-lg hover:bg-blue-50 font-ibm"
                        >
                          + Create New Trip
                        </button>
                      </div>
                    ) : (
                      d.live_trips.map(t => (
                        <div key={t.id} className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-gray-50/70 transition-colors">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-9 h-9 bg-blue-50 text-accent-blue rounded-lg flex items-center justify-center shrink-0">
                              <TruckIcon className="w-4 h-4" />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-slate-800 font-ibm">{t.trip_number}</span>
                                {t.vehicle_registration && (
                                  <span className="text-[11px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded font-mono font-medium">
                                    {t.vehicle_registration}
                                  </span>
                                )}
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded font-ibm border ${statusColor(t.status)}`}>
                                  {t.status}
                                </span>
                              </div>
                              <div className="flex items-center gap-1 text-xs text-slate-500 font-ibm mt-0.5">
                                <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
                                <span className="truncate">{t.from_location || '?'} → {t.to_location || '?'}</span>
                                {t.driver_name && <span className="text-slate-400">· {t.driver_name}</span>}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              onClick={() => setTrackingTrip(t)}
                              className="flex items-center gap-1 text-xs bg-slate-800 text-white px-2.5 py-1 rounded-md hover:bg-slate-700 font-ibm font-medium"
                              title="Track Vehicle"
                            >
                              <Navigation2 className="w-3 h-3" /> Track
                            </button>
                            <button
                              onClick={() => navigate('/fleet/trips')}
                              className="flex items-center gap-1 text-xs text-accent-blue hover:text-accent-blue/80 font-ibm font-medium p-1"
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </Card>
            ) : (
              // INTERNATIONAL: Active Air & Ocean Cargo Monitor
              <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden h-full flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                      <h3 className="text-base font-semibold text-text-dark font-manrope">Active Air & Ocean Cargo</h3>
                    </div>
                    <button
                      onClick={() => navigate('/dockets')}
                      className="text-xs text-accent-blue hover:text-accent-blue/80 font-ibm font-medium flex items-center gap-1"
                    >
                      View all <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>

                  <div className="divide-y divide-gray-50">
                    {(!d.active_shipments || d.active_shipments.length === 0) ? (
                      <div className="flex flex-col items-center justify-center py-10 text-center">
                        <Plane className="w-8 h-8 text-gray-300 mb-2" />
                        <p className="text-gray-500 font-ibm text-xs font-medium">No active international shipments in transit</p>
                        <button
                          onClick={() => navigate('/dockets/create')}
                          className="mt-3 text-xs text-accent-blue border border-accent-blue/30 px-3 py-1 rounded-lg hover:bg-blue-50 font-ibm"
                        >
                          + Create New Docket
                        </button>
                      </div>
                    ) : (
                      d.active_shipments.map(s => (
                        <div
                          key={s.id}
                          onClick={() => navigate(`/dockets/view/${s.id}`)}
                          className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-gray-50/70 transition-colors cursor-pointer"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                              s.shipment_type === 'AIR' ? 'bg-cyan-50 text-cyan-600' : 'bg-blue-50 text-blue-600'
                            }`}>
                              {s.shipment_type === 'AIR' ? <Plane className="w-4 h-4" /> : <Ship className="w-4 h-4" />}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-accent-blue font-ibm">{s.system_docket_no}</span>
                                {s.printed_docket_no && (
                                  <span className="text-[11px] text-slate-500 font-mono">({s.printed_docket_no})</span>
                                )}
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded font-ibm border ${statusColor(s.status)}`}>
                                  {s.status}
                                </span>
                              </div>
                              <p className="text-[11px] text-slate-400 font-ibm mt-0.5">
                                Created: {s.created_at ? s.created_at.split('T')[0] : '-'}
                              </p>
                            </div>
                          </div>

                          <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* RIGHT BLOCK (1/3 width): Fast Actions & Pipeline */}
          <div className="space-y-4">
            {/* Quick Actions Card */}
            <Card className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
              <h3 className="text-xs font-bold text-slate-900 font-manrope uppercase tracking-wider mb-2.5">
                Quick Operations
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {isInternationalCompany ? (
                  <>
                    <button
                      onClick={() => navigate('/estimates/add')}
                      className="p-2.5 bg-purple-50/70 hover:bg-purple-100 text-purple-800 rounded-lg flex flex-col items-center justify-center gap-1 transition-colors font-ibm text-[11px] font-semibold border border-purple-100 text-center"
                    >
                      <Calculator className="w-4 h-4 text-purple-600" />
                      <span>New Estimate</span>
                    </button>
                    <button
                      onClick={() => navigate('/dockets/create')}
                      className="p-2.5 bg-blue-50/70 hover:bg-blue-100 text-blue-800 rounded-lg flex flex-col items-center justify-center gap-1 transition-colors font-ibm text-[11px] font-semibold border border-blue-100 text-center"
                    >
                      <Plane className="w-4 h-4 text-blue-600" />
                      <span>Air/Sea Docket</span>
                    </button>
                    <button
                      onClick={() => navigate('/customers/add')}
                      className="p-2.5 bg-emerald-50/70 hover:bg-emerald-100 text-emerald-800 rounded-lg flex flex-col items-center justify-center gap-1 transition-colors font-ibm text-[11px] font-semibold border border-emerald-100 text-center"
                    >
                      <UserPlus className="w-4 h-4 text-emerald-600" />
                      <span>Add Customer</span>
                    </button>
                    <button
                      onClick={() => navigate('/booking/add')}
                      className="p-2.5 bg-amber-50/70 hover:bg-amber-100 text-amber-800 rounded-lg flex flex-col items-center justify-center gap-1 transition-colors font-ibm text-[11px] font-semibold border border-amber-100 text-center"
                    >
                      <Package className="w-4 h-4 text-amber-600" />
                      <span>New Booking</span>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => navigate('/dockets/create')}
                      className="p-2.5 bg-blue-50/70 hover:bg-blue-100 text-blue-800 rounded-lg flex flex-col items-center justify-center gap-1 transition-colors font-ibm text-[11px] font-semibold border border-blue-100 text-center"
                    >
                      <FileText className="w-4 h-4 text-blue-600" />
                      <span>New Docket</span>
                    </button>
                    <button
                      onClick={() => navigate('/fleet/trips/add')}
                      className="p-2.5 bg-emerald-50/70 hover:bg-emerald-100 text-emerald-800 rounded-lg flex flex-col items-center justify-center gap-1 transition-colors font-ibm text-[11px] font-semibold border border-emerald-100 text-center"
                    >
                      <TruckIcon className="w-4 h-4 text-emerald-600" />
                      <span>Create Trip</span>
                    </button>
                    <button
                      onClick={() => navigate('/booking/add')}
                      className="p-2.5 bg-amber-50/70 hover:bg-amber-100 text-amber-800 rounded-lg flex flex-col items-center justify-center gap-1 transition-colors font-ibm text-[11px] font-semibold border border-amber-100 text-center"
                    >
                      <Package className="w-4 h-4 text-amber-600" />
                      <span>New Booking</span>
                    </button>
                    <button
                      onClick={() => navigate('/estimates/add')}
                      className="p-2.5 bg-purple-50/70 hover:bg-purple-100 text-purple-800 rounded-lg flex flex-col items-center justify-center gap-1 transition-colors font-ibm text-[11px] font-semibold border border-purple-100 text-center"
                    >
                      <Calculator className="w-4 h-4 text-purple-600" />
                      <span>New Estimate</span>
                    </button>
                  </>
                )}
              </div>
            </Card>

            {/* Quotations / Estimates Pipeline Widget */}
            <Card className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <Calculator className="w-4 h-4 text-purple-600" />
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider font-manrope">
                    Estimates Funnel
                  </h4>
                </div>
                <button
                  onClick={() => navigate('/estimates')}
                  className="text-[11px] text-accent-blue font-semibold hover:underline"
                >
                  View ({d.estimates?.total || 0})
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2 text-center">
                <div className="bg-slate-50 p-2 rounded-lg border border-gray-100">
                  <p className="text-[10px] font-semibold text-slate-500 uppercase font-ibm">Drafts</p>
                  <p className="text-base font-bold text-slate-800 font-manrope">{d.estimates?.by_status?.DRAFT || 0}</p>
                </div>
                <div className="bg-blue-50/70 p-2 rounded-lg border border-blue-100">
                  <p className="text-[10px] font-semibold text-blue-600 uppercase font-ibm">Sent</p>
                  <p className="text-base font-bold text-blue-900 font-manrope">{d.estimates?.by_status?.SENT || 0}</p>
                </div>
                <div className="bg-emerald-50/70 p-2 rounded-lg border border-emerald-100">
                  <p className="text-[10px] font-semibold text-emerald-600 uppercase font-ibm">Accepted</p>
                  <p className="text-base font-bold text-emerald-900 font-manrope">{d.estimates?.by_status?.ACCEPTED || 0}</p>
                </div>
                <div className="bg-red-50/50 p-2 rounded-lg border border-red-100">
                  <p className="text-[10px] font-semibold text-red-500 uppercase font-ibm">Rejected</p>
                  <p className="text-base font-bold text-red-800 font-manrope">{d.estimates?.by_status?.REJECTED || 0}</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* ─── Visual Analytics & Operational Insights (Charts Hub) ───────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Monthly Shipment Volume Trend (7 cols ~ 58%) */}
        <Card className="lg:col-span-7 p-5 bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-3">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-accent-blue" />
                <div>
                  <h3 className="text-sm font-bold text-slate-900 font-manrope">Shipment Volume Trend</h3>
                  <p className="text-[11px] text-slate-400 font-ibm">
                    {isAllCompanies
                      ? 'Monthly shipments: Stavan Logistics vs. Stavan International'
                      : isLogisticsCompany
                      ? 'Monthly domestic freight movement (Road & Rail)'
                      : 'Monthly international cargo movement (Air & Ocean)'}
                  </p>
                </div>
              </div>
              <span className="text-[11px] font-semibold text-slate-600 bg-slate-100 px-2.5 py-0.5 rounded-full font-ibm">
                Last 6 Months
              </span>
            </div>

            <div className="h-[240px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.monthly_trend || []} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={{ stroke: '#f1f5f9' }} />
                  <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomBarTooltip />} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
                  {isAllCompanies ? (
                    <>
                      <Bar dataKey="slpl" name="Stavan Logistics" fill="#f97316" radius={[4, 4, 0, 0]} maxBarSize={24} />
                      <Bar dataKey="sillp" name="Stavan International" fill="#2563eb" radius={[4, 4, 0, 0]} maxBarSize={24} />
                    </>
                  ) : isLogisticsCompany ? (
                    <>
                      <Bar dataKey="ROAD" name="Road Freight" fill="#f97316" radius={[4, 4, 0, 0]} maxBarSize={24} />
                      <Bar dataKey="RAIL" name="Rail Freight" fill="#dc2626" radius={[4, 4, 0, 0]} maxBarSize={24} />
                    </>
                  ) : (
                    <>
                      <Bar dataKey="AIR" name="Air Cargo" fill="#06b6d4" radius={[4, 4, 0, 0]} maxBarSize={24} />
                      <Bar dataKey="SEA" name="Ocean Cargo" fill="#2563eb" radius={[4, 4, 0, 0]} maxBarSize={24} />
                    </>
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card>

        {/* Freight Mode Distribution Donut (5 cols ~ 42%) */}
        <Card className="lg:col-span-5 p-5 bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-2">
              <div className="flex items-center gap-2">
                <PieChartIcon className="w-4 h-4 text-purple-600" />
                <div>
                  <h3 className="text-sm font-bold text-slate-900 font-manrope">Freight Mode Split</h3>
                  <p className="text-[11px] text-slate-400 font-ibm">Operational multi-modal distribution</p>
                </div>
              </div>
              <span className="text-[11px] font-semibold text-purple-700 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-full font-ibm">
                {totalModalCount} Total
              </span>
            </div>

            <div className="h-[175px] w-full relative flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Tooltip content={<CustomPieTooltip />} />
                  <Pie
                    data={modeChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={48}
                    outerRadius={72}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {modeChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              {/* Centered Total Label */}
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-xl font-bold text-slate-800 font-manrope leading-none">{totalModalCount}</span>
                <span className="text-[9px] text-slate-400 font-ibm uppercase mt-0.5 font-medium">Shipments</span>
              </div>
            </div>

            {/* Custom Legend with Percentage Share */}
            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100 text-xs font-ibm">
              {modeChartData.map(item => {
                const pct = totalModalCount > 0 ? Math.round((item.value / totalModalCount) * 100) : 0;
                return (
                  <div key={item.name} className="flex items-center justify-between p-1.5 rounded-lg bg-gray-50/70">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                      <span className="text-slate-600 truncate text-[11px]">{item.name}</span>
                    </div>
                    <span className="font-bold text-slate-800 text-[11px] shrink-0">
                      {item.value} <span className="text-slate-400 font-normal">({pct}%)</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      </div>

      {/* ─── Bottom Section: Recent Dockets & Unified Activity Feed ───────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Dockets Table (2/3 width) */}
        <Card className="lg:col-span-2 p-5 bg-white rounded-xl border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-text-dark" />
              <h3 className="text-base font-semibold text-text-dark font-manrope">Recent Dockets</h3>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/dockets')}
              className="text-text-secondary hover:text-text-dark font-ibm text-xs h-7 px-2.5"
              data-testid="view-all-dockets-btn"
            >
              View All Dockets →
            </Button>
          </div>

          {(!d.recent_dockets || d.recent_dockets.length === 0) ? (
            <div className="text-center py-8 text-text-secondary font-ibm text-xs">No dockets created yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="recent-dockets-table">
                <thead className="border-b border-gray-100 bg-gray-50/50">
                  <tr className="text-left">
                    <th className="py-2.5 px-3 font-semibold text-slate-500 uppercase font-ibm">Date</th>
                    <th className="py-2.5 px-3 font-semibold text-slate-500 uppercase font-ibm">Docket No</th>
                    <th className="py-2.5 px-3 font-semibold text-slate-500 uppercase font-ibm">Printed Ref</th>
                    <th className="py-2.5 px-3 font-semibold text-slate-500 uppercase font-ibm">Mode</th>
                    <th className="py-2.5 px-3 font-semibold text-slate-500 uppercase font-ibm">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {d.recent_dockets.map(doc => (
                    <tr
                      key={doc.id}
                      onClick={() => navigate(`/dockets/view/${doc.id}`)}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <td className="py-2.5 px-3 text-slate-500 font-ibm">
                        {doc.created_at ? doc.created_at.split('T')[0] : '-'}
                      </td>
                      <td className="py-2.5 px-3 font-semibold text-accent-blue font-ibm">
                        {doc.system_docket_no}
                      </td>
                      <td className="py-2.5 px-3 text-slate-400 font-mono">
                        {doc.printed_docket_no || '-'}
                      </td>
                      <td className="py-2.5 px-3">
                        <span className="font-semibold text-slate-700 font-ibm">
                          {doc.shipment_type || '-'}
                        </span>
                      </td>
                      <td className="py-2.5 px-3">
                        <span className={`text-[10px] px-2 py-0.5 rounded font-ibm font-semibold border ${statusColor(doc.status)}`}>
                          {doc.status || '-'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Unified Operations Activity Stream (1/3 width) */}
        <Card className="p-5 bg-white rounded-xl border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-text-dark" />
              <h3 className="text-base font-semibold text-text-dark font-manrope">Recent Operations</h3>
            </div>
          </div>

          {activityItems.length === 0 ? (
            <div className="text-center py-8 text-text-secondary font-ibm text-xs">No recent activity recorded</div>
          ) : (
            <div className="space-y-3" data-testid="recent-activity-list">
              {activityItems.map(item => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.key}
                    onClick={item.onClick}
                    className="flex items-start gap-2.5 pb-2.5 border-b border-gray-50 last:border-0 hover:bg-gray-50/60 p-1 rounded-lg cursor-pointer transition-colors"
                  >
                    <div className="w-7 h-7 bg-slate-100 text-slate-600 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <p className="text-xs font-semibold text-slate-800 font-ibm truncate">{item.title}</p>
                        <span className={`text-[9px] px-1.5 py-0.2 rounded font-ibm font-semibold shrink-0 border ${statusColor(item.status)}`}>
                          {item.status}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 font-ibm truncate mt-0.5">{item.subtitle}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Vehicle GPS Tracking Modal */}
      {trackingTrip && (
        <VehicleTrackModal
          trip={trackingTrip}
          onClose={() => setTrackingTrip(null)}
        />
      )}
    </div>
  );
}
